import React, { useState, useEffect, useCallback } from "react";
import AdminPageHeader from "../../components/common/AdminPageHeader";
import Toast from "../../components/common/Toast";
import api from "../../api/axios";
import { useMainCompany } from "../../context/MainCompanyContext";
import { DASHBOARD_OPTION_KEYS, DASHBOARD_OPTION_LABELS, DASHBOARD_OPTION_LINKS } from "../../utils/dashboardOptions";
import { Save, ChevronUp, ChevronDown, GripVertical, Plus, X, Link2 } from "lucide-react";

/**
 * Manage Frontend Dashboard options per main company.
 * Backend API (save in DB):
 *   GET  /admin/dashboard-options?maincompanyid=<id>  → { data: ["seller", "buyer", ...] } or { options: [...] }
 *   PUT  /admin/dashboard-options  body: { maincompanyid, options: ["seller", "buyer", ...] }  → save and return success
 * Frontend dashboard (user) reads: GET /auth/dashboard-options?maincompanyid=<theme.id>  → same shape (from same DB).
 */

export default function AdminDashboardOptions() {
  const { selectedMain, mainCompanies } = useMainCompany();
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState({ visible: false, message: "", type: "success" });

  const selectedCompany = mainCompanies?.find((m) => m.id === selectedMain);

  const showToast = useCallback((message, type = "success") => {
    setToast({ visible: true, message, type });
    setTimeout(() => setToast((t) => ({ ...t, visible: false })), 3000);
  }, []);

  // Fetch dashboard options for selected main company
  useEffect(() => {
    let cancelled = false;
    if (!selectedMain) {
      setOptions([...DASHBOARD_OPTION_KEYS]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const fetchOptions = async () => {
      try {
        const { data } = await api.get("/admin/dashboard-options", {
          params: { maincompanyid: selectedMain },
        });
        const raw = data?.data ?? data?.options ?? data;
        const list = Array.isArray(raw) ? raw.filter((k) => DASHBOARD_OPTION_KEYS.includes(k)) : [];
        if (!cancelled) {
          setOptions(list);
        }
      } catch (err) {
        if (!cancelled) setOptions([...DASHBOARD_OPTION_KEYS]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchOptions();
    return () => { cancelled = true; };
  }, [selectedMain]);

  const moveUp = (index) => {
    if (index <= 0) return;
    setOptions((prev) => {
      const next = [...prev];
      [next[index - 1], next[index]] = [next[index], next[index - 1]];
      return next;
    });
  };

  const moveDown = (index) => {
    if (index >= options.length - 1) return;
    setOptions((prev) => {
      const next = [...prev];
      [next[index], next[index + 1]] = [next[index + 1], next[index]];
      return next;
    });
  };

  const addOption = (key) => {
    if (options.includes(key)) return;
    setOptions((prev) => [...prev, key]);
  };

  const removeOption = (key) => {
    setOptions((prev) => prev.filter((k) => k !== key));
  };

  const shownSet = new Set(options);
  const notShownKeys = DASHBOARD_OPTION_KEYS.filter((k) => !shownSet.has(k));

  const handleSave = async () => {
    if (!selectedMain) {
      showToast("Please select a main company.", "error");
      return;
    }
    setSaving(true);
    try {
      await api.put("/admin/dashboard-options", {
        maincompanyid: selectedMain,
        options,
      });
      showToast("Dashboard options saved successfully.");
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || "Save failed.";
      showToast(msg, "error");
    } finally {
      setSaving(false);
    }
  };

  if (!selectedMain) {
    return (
      <div className="p-4">
        <AdminPageHeader title="Frontend Dashboard Options" />
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-amber-800">
          Select a main company from the header dropdown to manage its dashboard options.
        </div>
        {toast.visible && <Toast message={toast.message} type={toast.type} visible={toast.visible} />}
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4">
      <AdminPageHeader
        title="Frontend Dashboard Options"
        actions={
          <button
            type="button"
            onClick={handleSave}
            disabled={saving || loading}
            className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
          >
            <Save className="w-4 h-4" />
            {saving ? "Saving..." : "Save"}
          </button>
        }
      />

      <p className="text-sm text-gray-600">
        Choose which links show on the frontend dashboard for{" "}
        <strong>{selectedCompany?.main_company_name ?? "this main company"}</strong>. Only options in &quot;Shown on dashboard&quot; will appear, in that order.
      </p>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Shown on dashboard */}
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-200 bg-green-50">
            <h2 className="text-sm font-semibold text-gray-800">Shown on dashboard</h2>
            <p className="text-xs text-gray-500 mt-0.5">These links will appear for this main company (drag order below)</p>
          </div>
          <div className="divide-y divide-gray-100 max-h-80 overflow-auto">
            {loading ? (
              <div className="px-4 py-8 text-center text-gray-500">Loading...</div>
            ) : options.length === 0 ? (
              <div className="px-4 py-6 text-center text-gray-500 text-sm">No options selected. Add from the right column.</div>
            ) : (
              options.map((key, index) => (
                <div
                  key={key}
                  className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50"
                >
                  <span className="text-gray-400" aria-hidden>
                    <GripVertical className="w-4 h-4" />
                  </span>
                  <div className="flex-1 min-w-0">
                    <span className="font-medium text-gray-800 block">
                      {DASHBOARD_OPTION_LABELS[key] ?? key}
                    </span>
                    <span className="text-xs text-gray-500 flex items-center gap-1 truncate" title={DASHBOARD_OPTION_LINKS[key]}>
                      <Link2 className="w-3 h-3 flex-shrink-0" />
                      {DASHBOARD_OPTION_LINKS[key] ?? key}
                    </span>
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => moveUp(index)}
                      disabled={index === 0}
                      className="p-1.5 rounded border border-gray-300 text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
                      title="Move up"
                    >
                      <ChevronUp className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => moveDown(index)}
                      disabled={index === options.length - 1}
                      className="p-1.5 rounded border border-gray-300 text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
                      title="Move down"
                    >
                      <ChevronDown className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => removeOption(key)}
                      className="p-1.5 rounded border border-red-200 text-red-600 hover:bg-red-50"
                      title="Remove from dashboard"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Not shown - available to add */}
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
            <h2 className="text-sm font-semibold text-gray-800">Not shown</h2>
            <p className="text-xs text-gray-500 mt-0.5">Add to &quot;Shown on dashboard&quot; to display for this company</p>
          </div>
          <div className="divide-y divide-gray-100 max-h-80 overflow-auto">
            {notShownKeys.length === 0 ? (
              <div className="px-4 py-6 text-center text-gray-500 text-sm">All options are shown on the dashboard.</div>
            ) : (
              notShownKeys.map((key) => (
                <div
                  key={key}
                  className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50"
                >
                  <div className="flex-1 min-w-0">
                    <span className="font-medium text-gray-800 block">
                      {DASHBOARD_OPTION_LABELS[key] ?? key}
                    </span>
                    <span className="text-xs text-gray-500 flex items-center gap-1 truncate" title={DASHBOARD_OPTION_LINKS[key]}>
                      <Link2 className="w-3 h-3 flex-shrink-0" />
                      {DASHBOARD_OPTION_LINKS[key] ?? key}
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => addOption(key)}
                    className="flex items-center gap-1 px-3 py-1.5 rounded border border-blue-300 text-blue-700 hover:bg-blue-50 text-sm font-medium"
                    title="Show on dashboard"
                  >
                    <Plus className="w-4 h-4" />
                    Add
                  </button>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {toast.visible && <Toast message={toast.message} type={toast.type} visible={toast.visible} />}
    </div>
  );
}
